import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useState } from 'react';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { useGetOrCreateDestination } from '../shared/useGetOrCreateDestination';
import { makeApplyGeometryDeps } from '../shared/applyGeometryDeps';
import { applyComputedGeometry } from '~/hooks/structure-set/applyComputedGeometry';
import { recordContourEdit } from '~/hooks/structure-set/recordContourEdit';

interface UseMarginApplicationOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
    onSuccess: () => void;
    updateView: () => void;
    /**
     * Optional: hand off a cached preview texture instead of recomputing.
     * Returns undefined if no cached result is available for the given input.
     */
    consumeSymmetricPreview?: (
        marginValueMm: number
    ) => ContourTexture | undefined;
    consumeAsymmetricPreview?: (
        negLpsMm: ReadonlyVec3,
        posLpsMm: ReadonlyVec3
    ) => ContourTexture | undefined;
    /**
     * Returns the padded "expanded" SDF the asymmetric pass reads from,
     * built once per source by the preview hook. Used when applying
     * without a cached preview to recompute.
     */
    getExpanded?: () => ContourTexture | undefined;
}

export function useMarginApplication({
    environment,
    inputRoi,
    onSuccess,
    updateView,
    consumeSymmetricPreview,
    consumeAsymmetricPreview,
    getExpanded,
}: UseMarginApplicationOptions) {
    const [isProcessing, setIsProcessing] = useState(false);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment,
    });

    const handleSymmetricApply = useCallback(
        async (
            marginMm: number,
            outputRoiNumber: number | null,
            newName?: string
        ) => {
            if (isProcessing) {
                enqueueSnackbar(
                    'Another margin operation is already in progress.',
                    {
                        variant: 'warning',
                    }
                );
                return;
            }

            if (!environment?.structureSet || !inputRoi?.texture) {
                enqueueSnackbar(
                    'Select a structure with contours to apply this operation.',
                    { variant: 'warning' }
                );
                return;
            }

            setIsProcessing(true);

            // The compute is identical in both branches; only the destination
            // (new vs overwrite) differs.
            const inputTexture = inputRoi.texture;
            const computeMarginTexture = async (): Promise<
                ContourTexture | undefined
            > => {
                const texture =
                    consumeSymmetricPreview?.(marginMm) ??
                    (await SDFOperations.applySymmetricMargin(
                        inputTexture,
                        marginMm
                    ));
                if (!texture) {
                    enqueueSnackbar('Failed to apply margin', {
                        variant: 'error',
                    });
                }
                return texture;
            };

            try {
                if (outputRoiNumber === null) {
                    if (!newName) {
                        enqueueSnackbar(
                            'A name is required to create a new structure',
                            { variant: 'error' }
                        );
                        return;
                    }
                    const marginTexture = await computeMarginTexture();
                    if (!marginTexture) {
                        return;
                    }
                    await applyComputedGeometry(
                        newName,
                        { texture: marginTexture, label: 'Symmetric Margin' },
                        makeApplyGeometryDeps({
                            environment,
                            inputRoi,
                            updateView,
                            getOrCreateDestination,
                        })
                    );
                } else {
                    const dest = await getOrCreateDestination(
                        outputRoiNumber,
                        newName,
                        inputRoi
                    );
                    if (!dest) {
                        return;
                    }
                    const marginTexture = await computeMarginTexture();
                    if (!marginTexture) {
                        return;
                    }
                    dest.replaceTexture(marginTexture, 'Symmetric Margin');
                    updateView();
                    recordContourEdit(
                        environment?.structureSet?.sopInstanceUID,
                        dest
                    );
                }
                enqueueSnackbar('Margin applied', { variant: 'success' });
                onSuccess();
            } catch (e) {
                console.error('Apply error:', e);
                enqueueSnackbar(
                    `Failed to apply margin: ${getErrorMessage(e)}`,
                    {
                        variant: 'error',
                    }
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [
            environment,
            inputRoi,
            isProcessing,
            getOrCreateDestination,
            updateView,
            onSuccess,
            consumeSymmetricPreview,
        ]
    );

    const handleAsymmetricApply = useCallback(
        async (
            negLpsMm: vec3,
            posLpsMm: vec3,
            outputRoiNumber: number | null,
            newName?: string
        ) => {
            if (isProcessing) {
                enqueueSnackbar(
                    'Another margin operation is already in progress.',
                    {
                        variant: 'warning',
                    }
                );
                return;
            }

            if (!environment?.structureSet || !inputRoi?.texture) {
                enqueueSnackbar(
                    'Select a structure with contours to apply this operation.',
                    { variant: 'warning' }
                );
                return;
            }

            setIsProcessing(true);

            const recomputeMargin = async (
                source: ContourTexture
            ): Promise<ContourTexture> => {
                enqueueSnackbar('Applying margin...', {
                    variant: 'info',
                });
                // TODO: improve kind of hacky rendering solutions should be removed and
                // heavy computation should be lifted away from blocking the main thread for smooth rendering.
                await waitForUI();
                return SDFOperations.applyAsymmetricMargin(
                    source,
                    negLpsMm,
                    posLpsMm
                );
            };
            const inputTexture = inputRoi.texture;

            try {
                if (outputRoiNumber === null) {
                    if (!newName) {
                        enqueueSnackbar(
                            'A name is required to create a new structure',
                            { variant: 'error' }
                        );
                        return;
                    }
                    const result =
                        consumeAsymmetricPreview?.(negLpsMm, posLpsMm) ??
                        (await recomputeMargin(inputTexture));
                    // applyAsymmetricMargin leaves the output covering the
                    // padded grid (margin + buffer). Crop to the image
                    // before persisting to the new ROI.
                    result.cropToImageBounds();
                    await applyComputedGeometry(
                        newName,
                        { texture: result, label: 'Asymmetric Margin' },
                        makeApplyGeometryDeps({
                            environment,
                            inputRoi,
                            updateView,
                            getOrCreateDestination,
                        })
                    );
                    enqueueSnackbar(`Applied margin to "${newName}"`, {
                        variant: 'success',
                    });
                    onSuccess();
                } else {
                    const dest = await getOrCreateDestination(
                        outputRoiNumber,
                        newName,
                        inputRoi
                    );
                    if (!dest || !dest.texture) {
                        return;
                    }
                    let result = consumeAsymmetricPreview?.(negLpsMm, posLpsMm);
                    if (!result) {
                        const expanded = getExpanded?.();
                        if (!expanded) {
                            enqueueSnackbar('Failed to apply margin', {
                                variant: 'error',
                            });
                            return;
                        }
                        result = await recomputeMargin(expanded);
                    }
                    // Crop to the image before persisting to the ROI (see the
                    // create branch above).
                    result.cropToImageBounds();
                    dest.replaceTexture(result, 'Asymmetric Margin');
                    updateView();
                    recordContourEdit(
                        environment?.structureSet?.sopInstanceUID,
                        dest
                    );
                    enqueueSnackbar(`Applied margin to "${dest.name}"`, {
                        variant: 'success',
                    });
                    onSuccess();
                }
            } catch (e) {
                console.error('Asymmetric apply error:', e);
                enqueueSnackbar(
                    `Failed to apply margin: ${getErrorMessage(e)}`,
                    {
                        variant: 'error',
                    }
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [
            environment,
            inputRoi,
            isProcessing,
            getOrCreateDestination,
            updateView,
            onSuccess,
            consumeAsymmetricPreview,
            getExpanded,
        ]
    );

    return {
        isProcessing,
        handleSymmetricApply,
        handleAsymmetricApply,
    };
}
